import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import {
  getChartModeTypes,
  getDefaultChartMode
} from '@aytm/app/stats/page/question-views/mx/components/chart/lib/chartModes'
import { calcHcAnswers } from '@aytm/app/stats/page/question-views/mx/components/chart/lib/hcAnswers'
import { getYAxisTitle } from '@aytm/app/stats/page/question-views/mx/components/chart/lib/axisConfig'

import { getChartTopsMapping } from '@aytm/app/stats/page/question-views/mx/lib/utils'
import { calcQuestionChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const MatrixChart = ({
  questionId,
  questionKey,
  qType,
  subType,
  viewMode,
  chartMode: chartModeProp,
  questionChartMode,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  answer,
  batches,
  topsMode,
  rType,
  degree,
  maxAnswerPosition,
  labelsMode,
  format,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  sortOrder,
  isTraitType,
  trait,
  features,
  viewOptionsDisabled,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const percentsOnly = format?.percentsOnly === true

  const chartModeTypes = useMemo(
    () => getChartModeTypes(viewMode, answer, rType, percentsOnly, isTraitType, trait),
    [viewMode, answer, rType, percentsOnly, isTraitType, trait]
  )

  const chartMode = useMemo(
    () =>
      getDefaultChartMode(
        calcQuestionChartMode(chartModeProp, questionChartMode, globalChartMode),
        viewMode,
        answer,
        rType,
        globalChartMode,
        percentsOnly,
        isTraitType,
        trait
      ),
    [chartModeProp, questionChartMode, viewMode, answer, rType, globalChartMode, percentsOnly, isTraitType, trait]
  )

  const chartTopsMapping = useMemo(() => {
    return viewMode === 'boxScore' ? getChartTopsMapping(qType, batches, maxAnswerPosition) : null
  }, [viewMode, qType, batches, maxAnswerPosition])

  const chartProps = useMemo(() => {
    const isScoreView = ['score', 'meanScore', 'boxScore'].includes(viewMode)
    const isAreaView = viewMode === 'area'
    const yAxisTitle = getYAxisTitle(viewMode, rType, chartTopsMapping, topsMode)

    if (isScoreView) {
      const { hcAnswers, yAxisLimits } = calcHcAnswers({
        viewMode,
        answer,
        batches,
        topsMode,
        degree
      })

      return {
        questionKey,
        chartMode,
        answerVariants: batches,
        answer: hcAnswers,
        qType,
        aType: ['ro', 'aro'].includes(qType) ? 'A' : 'SQ',
        sortOrder,
        yAxisTitle,
        yAxisLimits,
        colorTheme,
        chartLegendMode,
        viewMode,
        rType,
        zoomType: true,
        ignoreTraits: true,
        ignorePercents: viewMode !== 'boxScore',
        confidenceLevel,
        significanceLevel,
        confidenceLevels,
        format: { useSigTest: true }
      }
    }

    if (isAreaView) {
      return {
        questionKey,
        chartMode: 'area',
        batches,
        answer,
        qType,
        labelsMode,
        colorTheme,
        chartLegendMode,
        sortOrder
      }
    }

    return {
      questionKey,
      chartMode: `stacked_${chartMode}`,
      batches,
      answer,
      qType,
      yAxisTitle,
      viewMode,
      rType,
      labelsMode,
      colorTheme,
      chartLegendMode,
      sortOrder,
      zoomType: false
    }
  }, [
    viewMode,
    chartMode,
    answer,
    batches,
    qType,
    topsMode,
    degree,
    rType,
    chartTopsMapping,
    labelsMode,
    colorTheme,
    chartLegendMode,
    confidenceLevel,
    significanceLevel,
    confidenceLevels,
    sortOrder
  ])

  return (
    <ChartContainer
      chartRef={chartRef}
      {...chartProps}
      questionId={questionId}
      qSubType={subType}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      features={features}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      onZoomChange={onZoomChange}
    />
  )
}

MatrixChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  subType: PropTypes.string,
  viewMode: PropTypes.string.isRequired,
  chartMode: PropTypes.string,
  // the mode the question declares in its data, drawn when the reader picked none
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  answer: answerPropType,
  batches: PropTypes.array,
  topsMode: PropTypes.string,
  rType: PropTypes.string,
  degree: PropTypes.number,
  maxAnswerPosition: PropTypes.number,
  labelsMode: PropTypes.string,
  format: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  sortOrder: PropTypes.string,
  isTraitType: PropTypes.bool,
  trait: PropTypes.object,
  features: PropTypes.object,
  viewOptionsDisabled: PropTypes.bool,
  onChartReady: PropTypes.func
}

export default MatrixChart
